import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Eye, Square } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, LoadingState, ErrorState, EmptyState, Card, SectionTitle, Field, FormError, SubmitButton, StatusBadge } from "@/components/common";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg, setApiGarage } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { GARAGE_KEY } from "@/context/AuthContext";
import { t } from "@/i18n";

export default function AdminSupport() {
  const navigate = useNavigate();
  const garages = useFetch("/admin/garages");
  const sessions = useFetch("/admin/support-sessions");
  const [f, setF] = useState({ garage_id: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const open = async (e) => {
    e.preventDefault();
    if (!f.garage_id) return setError(t("support.chooseGarage"));
    if (f.reason.trim().length < 10) return setError(t("support.reasonRequired"));
    setBusy(true);
    setError("");
    try {
      await api.post("/admin/support-sessions", f);
      toast.success(t("support.opened"));
      setF({ garage_id: "", reason: "" });
      sessions.reload();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const end = async (id) => {
    try {
      await api.post(`/admin/support-sessions/${id}/end`);
      toast.success(t("support.ended"));
      sessions.reload();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const view = (gid) => {
    localStorage.setItem(GARAGE_KEY, gid);
    setApiGarage(gid);
    navigate("/pro");
  };

  return (
    <div>
      <PageHeader eyebrow={t("admin.area")} title={t("admin.nav.support")} subtitle={t("support.subtitle")} />
      <div className="grid gap-6 lg:grid-cols-[1fr_1.4fr]">
        <Card testId="open-support-session-card">
          <SectionTitle hint={t("support.policy")}>{t("support.openTitle")}</SectionTitle>
          <form onSubmit={open} className="space-y-4">
            <FormError message={error} />
            <Field label={t("admin.garage")}>
              <Select value={f.garage_id} onValueChange={(v) => setF({ ...f, garage_id: v })}>
                <SelectTrigger data-testid="support-garage-select"><SelectValue placeholder={t("support.chooseGarage")} /></SelectTrigger>
                <SelectContent>{(garages.data || []).map((g) => <SelectItem key={g.id} value={g.id} data-testid={`support-garage-option-${g.slug}`}>{g.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label={t("support.reason")} htmlFor="sup-reason" hint={t("support.reasonHint")}>
              <Textarea id="sup-reason" required minLength={10} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="support-reason-input" />
            </Field>
            <SubmitButton busy={busy} data-testid="open-support-session-button"><ShieldCheck className="mr-2 h-4 w-4" /> {t("support.openButton")}</SubmitButton>
          </form>
        </Card>
        <div>
          <h2 className="mb-4 text-lg font-bold text-slate-900">{t("support.sessions")}</h2>
          {sessions.loading && !sessions.data && <LoadingState />}
          {sessions.error && <ErrorState message={sessions.error} onRetry={sessions.reload} />}
          {sessions.data?.length === 0 && <EmptyState icon={ShieldCheck} title={t("support.none")} text={t("support.noneText")} />}
          <div className="space-y-3" data-testid="support-sessions-list">
            {sessions.data?.map((s) => (
              <div key={s.id} className="gf-card p-5" data-testid={`support-session-${s.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-semibold text-slate-900">{s.garage_name}</p>
                  <StatusBadge status={s.active ? "active" : "ended"} />
                </div>
                <p className="mt-2 text-sm text-slate-700">“{s.reason}”</p>
                <p className="mt-2 text-xs text-slate-500">
                  {t("support.timing", { start: fmtDateTime(s.started_at), end: fmtDateTime(s.ended_at || s.expires_at) })}
                </p>
                {s.active && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button size="sm" onClick={() => view(s.garage_id)} data-testid={`support-view-${s.id}`}><Eye className="mr-1.5 h-4 w-4" /> {t("support.view")}</Button>
                    <Button size="sm" variant="outline" onClick={() => end(s.id)} data-testid={`support-end-${s.id}`}><Square className="mr-1.5 h-3.5 w-3.5" /> {t("support.end")}</Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
